import { Radio, RadioGroup, RadioGroupLabel } from '@headlessui/react';
import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { Form } from '../ui';
import { classNames } from '../../utils/class-names';
import { Brief } from '../../services/models/brief';
import { DayOfWeek } from '../../services/models/day-of-week';

export interface EditBriefScheduleProps {
  brief: Brief;
  update: (hour: number, selectedDays: DayOfWeek[]) => void;
}

const weekdays = [
  DayOfWeek.Monday,
  DayOfWeek.Tuesday,
  DayOfWeek.Wednesday,
  DayOfWeek.Thursday,
  DayOfWeek.Friday,
];

const allDaysOfWeek = [DayOfWeek.Sunday, ...weekdays, DayOfWeek.Saturday];

enum BriefFrequency {
  Daily = 'daily',
  Weekday = 'weekday',
  Custom = 'custom',
}

const allFrequencies = [BriefFrequency.Daily, BriefFrequency.Weekday, BriefFrequency.Custom];

const EditBriefSchedule: FunctionComponent<EditBriefScheduleProps> = ({ brief, update }) => {
  const [briefFrequency, setBriefFrequency] = useState(BriefFrequency.Daily);
  const [daysOfWeek, setDaysOfWeek] = useState<DayOfWeek[]>(weekdays);
  const [briefHour, setBriefHour] = useState(9);
  // Custom is a UI mode, not a distinct day set: any day set the server echoes
  // back re-derives as Daily/Weekday/Custom below, so an explicit Custom pick
  // must be remembered or the derivation would snap the radio back. Editing
  // the days clears it again — a set grown into all seven days reads as Daily.
  const userSelectedCustom = useRef(false);

  useEffect(() => {
    const getFrequencyFromSelectedDays = () => {
      if (brief.daysOfWeek.length === 7) {
        return BriefFrequency.Daily;
      } else if (
        brief.daysOfWeek.length === 5 &&
        weekdays.every(x => brief.daysOfWeek.some(day => x === day))
      ) {
        return BriefFrequency.Weekday;
      }
      return BriefFrequency.Custom;
    };
    setDaysOfWeek(brief.daysOfWeek || []);
    setBriefFrequency(
      userSelectedCustom.current ? BriefFrequency.Custom : getFrequencyFromSelectedDays()
    );
  }, [brief.daysOfWeek]);

  useEffect(() => {
    setBriefHour(brief.briefHour);
  }, [brief.briefHour]);

  const handleBriefFrequencyChange = (val: BriefFrequency) => {
    if (val === BriefFrequency.Custom) {
      // Custom keeps the current day set, so there is nothing to persist —
      // the day toggles below drive the mutation from here.
      userSelectedCustom.current = true;
      setBriefFrequency(BriefFrequency.Custom);
      return;
    }
    userSelectedCustom.current = false;
    setBriefFrequency(val);
    const newDaysOfTheWeek = val === BriefFrequency.Weekday ? [...weekdays] : [...allDaysOfWeek];
    // A preset that already matches the current days has nothing to persist
    // (e.g. leaving a Custom selection that spans all seven days).
    if (
      newDaysOfTheWeek.length === daysOfWeek.length &&
      newDaysOfTheWeek.every(day => daysOfWeek.includes(day))
    ) {
      return;
    }
    update(getBriefHour(), newDaysOfTheWeek);
  };

  const handleBriefHourChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(event.target.value);
    update(newValue, daysOfWeek);
  };

  const getBriefHour = () => {
    return briefHour || 9;
  };

  const handleToggleDay = (dayOfWeeks: DayOfWeek[]) => {
    // Validate the proposed selection, not the current one: a brief must
    // keep at least one day, but a single-day brief may still ADD days.
    if (dayOfWeeks.length > 0) {
      // Editing the days hands control back to the derivation, so a set
      // toggled into all seven days (or Mon–Fri) reads as Daily (Weekdays).
      userSelectedCustom.current = false;
      update(getBriefHour(), dayOfWeeks);
    }
  };

  const renderDaySelector = () => {
    const fetchLabel = (source: DayOfWeek) => {
      switch (source) {
        case DayOfWeek.Monday:
          return 'M';
        case DayOfWeek.Tuesday:
          return 'T';
        case DayOfWeek.Wednesday:
          return 'W';
        case DayOfWeek.Thursday:
          return 'Th';
        case DayOfWeek.Friday:
          return 'F';
        case DayOfWeek.Saturday:
        case DayOfWeek.Sunday:
          return 'S';
      }
    };

    return (
      <div className="mb-5">
        <div className="mb-5 font-['Ubuntu'] text-[18px] font-bold tracking-[0.5px] text-gray-700">
          Custom
        </div>
        <div className="inline-flex rounded-md shadow-xs" role="group">
          {allDaysOfWeek.map(x => {
            const selected = daysOfWeek.includes(x);
            return (
              <button
                key={x}
                type="button"
                // Distinct names for screen readers: Saturday and Sunday both
                // render as a bare "S".
                aria-label={x.charAt(0) + x.slice(1).toLowerCase()}
                aria-pressed={selected}
                className={classNames(
                  'px-3 py-2 text-sm font-medium border border-secondary -ml-px first:ml-0 first:rounded-l-md last:rounded-r-md focus:outline-hidden focus:ring-2 focus:ring-secondary',
                  selected
                    ? 'bg-secondary text-white'
                    : 'bg-white text-secondary hover:bg-secondary hover:text-white'
                )}
                onClick={() =>
                  handleToggleDay(
                    selected ? daysOfWeek.filter(day => day !== x) : [...daysOfWeek, x]
                  )
                }
              >
                {fetchLabel(x)}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const getTimeSection = () => {
    return (
      <div className="mb-5">
        <div className="mb-5 font-['Ubuntu'] text-[18px] font-bold tracking-[0.5px] text-gray-700">
          What time you want for your News Brief?
        </div>
        <Form.Control
          size="sm"
          as="select"
          value={briefHour}
          onChange={(e: any) => handleBriefHourChange(e)}
          className="w-auto!"
        >
          <option value="24">12 am</option>
          <option value="1">1 am</option>
          <option value="2">2 am</option>
          <option value="3">3 am</option>
          <option value="4">4 am</option>
          <option value="5">5 am</option>
          <option value="6">6 am</option>
          <option value="7">7 am</option>
          <option value="8">8 am</option>
          <option value="9">9 am</option>
          <option value="10">10 am</option>
          <option value="11">11 am</option>
          <option value="12">12 pm</option>
          <option value="13">1 pm</option>
          <option value="14">2 pm</option>
          <option value="15">3 pm</option>
          <option value="16">4 pm</option>
          <option value="17">5 pm</option>
          <option value="18">6 pm</option>
          <option value="19">7 pm</option>
          <option value="20">8 pm</option>
          <option value="21">9 pm</option>
          <option value="22">10 pm</option>
          <option value="23">11 pm</option>
        </Form.Control>
      </div>
    );
  };

  const getFrequency = () => {
    const fetchLabel = (source: BriefFrequency) => {
      switch (source) {
        case BriefFrequency.Daily:
          return 'Daily';
        case BriefFrequency.Weekday:
          return 'Weekdays';
        case BriefFrequency.Custom:
          return 'Custom';
      }
    };
    return (
      <div className="mb-5">
        <RadioGroup value={briefFrequency} onChange={handleBriefFrequencyChange}>
          <RadioGroupLabel className="mb-5 block font-['Ubuntu'] text-[18px] font-bold tracking-[0.5px] text-gray-700">
            How often?
          </RadioGroupLabel>
          {/* Headless UI supplies real radio semantics: roving tabindex and
              arrow-key navigation, which hand-rolled role="radio" buttons
              lack. */}
          <div className="inline-flex rounded-md shadow-xs">
            {allFrequencies.map(x => (
              <Radio
                key={x}
                value={x}
                className={({ checked }) =>
                  classNames(
                    'cursor-pointer px-3 py-2 text-sm font-medium border border-secondary -ml-px first:ml-0 first:rounded-l-md last:rounded-r-md focus:outline-hidden focus:ring-2 focus:ring-secondary',
                    checked
                      ? 'bg-secondary text-white'
                      : 'bg-white text-secondary hover:bg-secondary hover:text-white'
                  )
                }
              >
                {fetchLabel(x)}
              </Radio>
            ))}
          </div>
        </RadioGroup>
      </div>
    );
  };

  return (
    <div className="mx-auto mb-[30px] flex max-w-[260px] flex-col items-start justify-center">
      {getTimeSection()}
      {getFrequency()}
      {renderDaySelector()}
    </div>
  );
};

export default EditBriefSchedule;
